import { RecurringRequestStatus } from '@back-office/http';
import { Instance } from 'mobx-state-tree';
import ms from 'ms';
import { useEffect, useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useHistory } from 'react-router-dom';
import { useMount, useToggle, useUnmount } from 'react-use';
import { STATUS_CODE_NOT_AUTHORIZED } from '../../constants';
import useInject from '../../hooks/useInject';
import AuthModel from '../../stores/auth.store';
import CancelReasonTypesModel from '../../stores/cancel-reason-types.store';
import RecurringDetailModel from '../../stores/recurring-detail.store';
import RejectReasonTypesModel from '../../stores/reject-reason-types.store';

type UseViewModelProps = {
  recurringId: string | null;
};

type UseViewModelReturn = {
  recurringDetail: Instance<typeof RecurringDetailModel>;
  isLoading: boolean;
  approveDialog: boolean;
  toggleApproveDialog: (value: boolean) => void;
  rejectDialog: boolean;
  toggleRejectDialog: (value: boolean) => void;
  cancelDialog: boolean;
  toggleCancelDialog: (value: boolean) => void;
  alertMessage: string | undefined;
  alertStatus: RecurringRequestStatus;
  isRecurringPending: boolean;
  isRecurringApproved: boolean;
  rejectReasonTypes: Instance<typeof RejectReasonTypesModel>;
  cancelReasonTypes: Instance<typeof CancelReasonTypesModel>;
  onClickReject: () => Promise<void>;
  onClickCancel: () => Promise<void>;
  handleCancelRecurring: (reason: string) => Promise<void>;
  handleApproveRecurring: () => Promise<void>;
  handleRejectRecurring: (reason: string) => Promise<void>;
  sortAndFilterRejectReasonType: (
    array: {
      id: string;
      name: string;
      description: string;
    }[]
  ) => string[];
  auth: Instance<typeof AuthModel>;
};

const useViewModel = ({
  recurringId,
}: UseViewModelProps): UseViewModelReturn => {
  const {
    getRecurringDetail,
    recurringDetail,
    isLoading,
    setLoading,
    updateRecurring,
    rejectReasonTypes,
    getRejectReasonTypes,
    cancelReasonTypes,
    getCancelReasonTypes,
    auth,
  } = useInject((root) => root);
  const history = useHistory();
  const [approveDialog, toggleApproveDialog] = useToggle(false);
  const [rejectDialog, toggleRejectDialog] = useToggle(false);
  const [cancelDialog, toggleCancelDialog] = useToggle(false);
  const [alertMessage, setAlertMessage] = useState<string>();
  const [alertStatus, setAlertStatus] =
    useState<RecurringRequestStatus>('Approved');

  const { t } = useTranslation();

  useMount(() => {
    if (recurringId) {
      getRecurringDetail(recurringId);
    } else {
      history.push('/');
    }
  });

  useUnmount(() => {
    recurringDetail.reset();
  });

  const isRecurringPending = useMemo(
    () => recurringDetail.status === 'Pending',
    [recurringDetail?.status]
  );

  const isRecurringApproved = useMemo(
    () => recurringDetail.status === 'Approved',
    [recurringDetail?.status]
  );

  const sortAndFilterRejectReasonType = (
    array: {
      id: string;
      name: string;
      description: string;
    }[]
  ): string[] => {
    const rejectTypesFilter: string[] = array.reduce(
      (requestType: string[], obj) => {
        requestType.push(obj.name);

        return requestType;
      },
      []
    );
    return rejectTypesFilter;
  };

  const onClickReject = async (): Promise<void> => {
    await getRejectReasonTypes();
    toggleRejectDialog(true);
  };

  const onClickCancel = async (): Promise<void> => {
    await getCancelReasonTypes();
    toggleCancelDialog(true);
  };

  const handleRecurringPaymentAction = async (
    actionType: RecurringRequestStatus,
    id: string,
    reason: string
  ): Promise<void> => {
    setLoading(true);
    setAlertStatus(actionType);
    try {
      const response = await updateRecurring(id, actionType, reason);
      const { data } = response;
      if (data?.updateRecurringRequestForSubMerchant) {
        setAlertMessage(
          t(`dialog.success_${actionType.toLowerCase()}_enrollment`)
        );
        await getRecurringDetail(id);
      }
    } catch (error) {
      if (error !== STATUS_CODE_NOT_AUTHORIZED) {
        await getRecurringDetail(id);
      }
    } finally {
      setLoading(false);
    }
  };

  const handleCancelRecurring = async (reason: string): Promise<void> => {
    if (recurringId) {
      await handleRecurringPaymentAction('Cancelled', recurringId, reason);
      toggleCancelDialog(false);
    }
  };

  const handleApproveRecurring = async (): Promise<void> => {
    if (recurringId) {
      await handleRecurringPaymentAction('Approved', recurringId, '-');
      toggleApproveDialog(false);
    }
  };

  const handleRejectRecurring = async (reason: string): Promise<void> => {
    if (recurringId) {
      await handleRecurringPaymentAction('Rejected', recurringId, reason);
      toggleRejectDialog(false);
    }
  };

  useEffect(() => {
    if (alertMessage) {
      const timeout = setInterval(() => {
        clearInterval(timeout);
        setAlertMessage(undefined);
      }, ms('5s'));
    }
  }, [alertMessage]);

  return {
    recurringDetail,
    isLoading,
    approveDialog,
    toggleApproveDialog,
    rejectDialog,
    toggleRejectDialog,
    cancelDialog,
    toggleCancelDialog,
    alertMessage,
    alertStatus,
    isRecurringPending,
    isRecurringApproved,
    rejectReasonTypes,
    cancelReasonTypes,
    onClickReject,
    onClickCancel,
    handleCancelRecurring,
    handleApproveRecurring,
    handleRejectRecurring,
    sortAndFilterRejectReasonType,
    auth,
  };
};

export default useViewModel;
